import type { ConnectError } from '@khal-os/sdk/app';
import { create } from 'zustand';

/**
 * Retry-loop statuses:
 *  - `idle`           no active connection attempt
 *  - `connecting`     an attempt is in flight
 *  - `retry_waiting`  a transient failure occurred; countdown until next attempt
 *  - `retry_failed`   exhausted all attempts for a transient error
 *  - `fatal`          stopped early — lastError is non-retryable (isFatal=true)
 *  - `connected`      happy path
 */
export type RetryStatus = 'idle' | 'connecting' | 'retry_waiting' | 'retry_failed' | 'fatal' | 'connected';

export interface RetryInfo {
	active: boolean;
	status: RetryStatus;
	retryCount: number;
	retryIn: number;
	maxRetries: number;
	onManualRetry: (() => void) | null;
	/**
	 * Last ConnectError observed from the transport. Populated on transient
	 * failures too (so retry UIs can show "last error was X, retrying in Ys").
	 * When `status === 'fatal'`, this is always present.
	 */
	lastError?: ConnectError;
}

export const useRetryStore = create<RetryInfo>(() => ({
	active: false,
	status: 'idle',
	retryCount: 0,
	retryIn: 0,
	maxRetries: 10,
	onManualRetry: null,
	lastError: undefined,
}));
